/**
 * The checklist record on a proposed fix (ticks with who and when, fill-in
 * values), read and written through the workspace server's tenant-scoped
 * proxy. Writes are optimistic: the row changes at once, the server's answer
 * replaces it, and a refusal puts the previous state back.
 *
 * @module state/findingFixReview
 */
import { useAtomValue } from "@effect/atom-react";
import {
  FixReviewView,
  type DocumentChangesView,
  type FixReviewWrite,
} from "@repo/shared/findingChanges";
import * as Effect from "effect/Effect";
import * as Schema from "effect/Schema";
import { AsyncResult, Atom } from "effect/unstable/reactivity";
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { resolvePrimaryEnvironmentHttpUrl } from "../environments/primary/target";
import { appAtomRegistry } from "../rpc/atomRegistry";
import { fetchComplianceJson } from "./complianceFetch";
import { complianceQueryAtom } from "./complianceLive";
import { applyFixReviewWrite, createInFlightWrites, revertFixReviewWrite } from "./findingFixReview.logic";
import { noteLocalReviewWrite } from "./reviewCountNotifications";

type Address = readonly [
  organizationId: string,
  policyId: string,
  reviewId: string,
  proposalId: string,
  kind: "finding" | "recommendation",
];

/** The proxy URL for one proposal's checklist record. */
export function fixReviewUrl([organizationId, policyId, reviewId, proposalId, kind]: Address): string {
  return resolvePrimaryEnvironmentHttpUrl(`/api/amoreg/regulation/${kind}-fix-review`, {
    organizationId,
    policyId,
    [kind === "finding" ? "findingId" : "recommendationId"]: reviewId,
    proposalId,
  });
}

/** Where a proposal's checklist lives: its finding or suggestion, document and organisation. */
export function fixReviewAddress(organizationId: string, proposal: DocumentChangesView): Address {
  return "findingId" in proposal
    ? [organizationId, proposal.policyId, proposal.findingId, proposal.id, "finding"]
    : [organizationId, proposal.policyId, proposal.recommendationId, proposal.id, "recommendation"];
}

const decode = Schema.decodeUnknownEffect(FixReviewView);
const decodeSync = Schema.decodeUnknownSync(FixReviewView);

export const fixReviewAtom = Atom.family((key: string) => {
  const address = JSON.parse(key) as Address;
  const [organizationId, policyId, reviewId, proposalId] = address;
  return complianceQueryAtom(
    organizationId,
    Effect.gen(function* () {
      if (!organizationId || !policyId || !reviewId || !proposalId) return null;
      return yield* fetchComplianceJson(fixReviewUrl(address), decode);
    }),
  );
});

/** Send one write. Resolves to the stored record, or a message to show. */
export async function writeFixReview(
  address: Address,
  write: FixReviewWrite,
): Promise<{ readonly ok: true; readonly review: FixReviewView } | { readonly ok: false; readonly message: string }> {
  noteLocalReviewWrite();
  try {
    const response = await fetch(fixReviewUrl(address), {
      method: "POST",
      credentials: "include",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(write),
    });
    const body = await response.text().catch(() => "");
    let parsed: unknown = null;
    try {
      parsed = JSON.parse(body);
    } catch {
      parsed = null;
    }
    if (response.ok) {
      try {
        return { ok: true, review: decodeSync(parsed) };
      } catch {
        return { ok: false, message: "The checklist answered in an unexpected form." };
      }
    }
    if (response.status === 403) return { ok: false, message: "Only organization admins can tick or fill the checklist." };
    const message =
      parsed && typeof parsed === "object" && "message" in parsed && typeof parsed.message === "string"
        ? parsed.message
        : null;
    return { ok: false, message: message ?? "The checklist could not be updated." };
  } catch {
    return { ok: false, message: "The checklist could not be updated." };
  }
}

/**
 * One proposal's checklist record with optimistic writes. `onError` hears a
 * refused write after the row has been put back.
 */
export function useFixReview(
  organizationId: string,
  proposal: DocumentChangesView | null,
  options: { readonly actorName?: string | null; readonly onError?: (message: string) => void } = {},
) {
  const address = useMemo(
    () => (proposal ? fixReviewAddress(organizationId, proposal) : null),
    [organizationId, proposal],
  );
  const key = JSON.stringify(address ?? ["", "", "", "", "finding"]);
  const atom = useMemo(() => fixReviewAtom(key), [key]);
  const result = useAtomValue(atom);
  const value = AsyncResult.value(result);
  const data = value._tag === "Some" ? value.value : null;
  const fetched = data && !("unavailable" in data) ? data : null;

  // What the page shows while writes are in flight; null means "the fetched record".
  const [override, setOverride] = useState<FixReviewView | null>(null);
  const [inFlight] = useState(createInFlightWrites);
  const latest = useRef<FixReviewView | null>(null);
  // The proposal on screen now. A write that answers after the page moved on
  // to another proposal must not touch that one's checklist.
  // Set on commit, so a render React abandons cannot reroute a reply.
  const currentKey = useRef(key);
  useLayoutEffect(() => {
    currentKey.current = key;
  }, [key]);
  const review = override ?? fetched;
  latest.current = review;
  // A fresh read (this write's revision, or someone else's) wins once nothing
  // is in flight for this proposal.
  useEffect(() => {
    if (inFlight.idle(currentKey.current)) setOverride(null);
  }, [fetched, inFlight]);
  useEffect(() => setOverride(null), [key]);

  const { actorName = null, onError } = options;
  const send = useCallback(
    async (write: FixReviewWrite) => {
      if (!address) return;
      const before = latest.current ?? { proposalId: address[3], confirmations: {}, fills: {} };
      const optimistic = applyFixReviewWrite(before, write, { name: actorName, at: new Date().toISOString() });
      if (optimistic === before) return;
      const sentKey = key;
      inFlight.begin(sentKey);
      // A second write sent before the next render builds on this one.
      latest.current = optimistic;
      setOverride(optimistic);
      const outcome = await writeFixReview(address, write);
      inFlight.end(sentKey);
      if (currentKey.current !== sentKey) {
        // The page shows another proposal now: refresh this one's cache, keep
        // the result out of the other checklist, still report a refusal.
        appAtomRegistry.refresh(atom);
        if (!outcome.ok) onError?.(outcome.message);
        return;
      }
      if (outcome.ok) {
        // The server's record wins, unless another write is still on its way.
        if (inFlight.idle(sentKey)) setOverride(outcome.review);
        appAtomRegistry.refresh(atom);
        return;
      }
      // Put back only this write's change, keeping any other write that landed meanwhile.
      setOverride((current) => revertFixReviewWrite(current ?? optimistic, before, write));
      // A rollback restores what this write saw, which can be another write's
      // unconfirmed tick; a fresh read settles on the stored record once idle.
      appAtomRegistry.refresh(atom);
      onError?.(outcome.message);
    },
    [address, actorName, atom, inFlight, key, onError],
  );

  return {
    review,
    loading: result._tag === "Initial" || (result.waiting && !data),
    unavailable: result._tag === "Failure" || Boolean(data && "unavailable" in data),
    confirm: (index: number, confirmed: boolean) => send({ action: "confirm", index, confirmed }),
    fill: (key: string, value: string) => send({ action: "fill", key, value }),
  };
}
